import { useQueryState, parseAsString, parseAsInteger } from "nuqs"
import { RefreshCw } from "lucide-react"

import { trpc } from "../lib/trpc"

/**
 * AuditLogsScreen - View system audit logs (super admin only)
 */
function AuditLogsScreen() {
  const [entityType, setEntityType] = useQueryState("entityType", parseAsString.withDefault(""))
  const [action, setAction] = useQueryState("action", parseAsString.withDefault(""))
  const [userId, setUserId] = useQueryState("userId", parseAsString.withDefault(""))
  const [entityId, setEntityId] = useQueryState("entityId", parseAsString.withDefault(""))
  const [page, setPage] = useQueryState("page", parseAsInteger.withDefault(1))
  const limit = 20

  // Build filters object for the query
  const filters = {
    page,
    limit,
    ...(entityType ? { entityType } : {}),
    ...(action ? { action } : {}),
    ...(userId ? { userId } : {}),
    ...(entityId ? { entityId } : {}),
  }

  // Fetch audit logs
  const { data: auditLogs, isLoading, refetch } = trpc.accessControl.getAuditLogs.useQuery(filters)

  // Fetch filter options
  const { data: filterOptions } = trpc.accessControl.getAuditLogFilters.useQuery()

  const handleFilterChange = (key: string, value: string) => {
    void setPage(1) // Reset page when filters change
    const setters: Record<string, (val: string, opts?: any) => void> = {
      entityType: (val, opts) => {
        void setEntityType(val, opts)
      },
      action: (val, opts) => {
        void setAction(val, opts)
      },
      userId: (val, opts) => {
        void setUserId(val, opts)
      },
      entityId: (val, opts) => {
        void setEntityId(val, opts)
      },
    }
    const setter = setters[key]
    if (setter) {
      setter(value || "", { history: "replace" })
    }
  }
  const handlePageChange = (newPage: number) => {
    void setPage(newPage)
  }

  const resetFilters = () => {
    void setEntityType("", { history: "replace" })
    void setAction("", { history: "replace" })
    void setUserId("", { history: "replace" })
    void setEntityId("", { history: "replace" })
    void setPage(1)
  }

  const formatDate = (dateString: string | Date) => {
    return new Date(dateString).toLocaleString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    })
  }

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <div className="bg-card rounded-lg border border-border shadow-sm">
        <div className="px-6 py-4">
          {/* Header */}
          <div className="flex flex-col gap-4 mb-6">
            <div className="flex items-center justify-between pb-4 border-b border-border">
              <h2 className="text-lg font-medium text-foreground">Audit Logs</h2>
              <div className="flex gap-2">
                <button
                  onClick={() => refetch()}
                  className="inline-flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-foreground bg-secondary hover:bg-secondary/80 rounded-md transition-colors"
                >
                  <RefreshCw className="w-4 h-4" />
                  Refresh
                </button>
                <button
                  onClick={resetFilters}
                  className="inline-flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-foreground bg-secondary hover:bg-secondary/80 rounded-md transition-colors"
                >
                  Reset Filters
                </button>
              </div>
            </div>

            {/* Filters */}
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4">
              <div className="flex flex-col">
                <label className="mb-1 text-xs font-medium text-muted-foreground">Action</label>
                <select
                  value={action}
                  onChange={(e) => handleFilterChange("action", e.target.value)}
                  className="px-3 py-2 text-sm bg-background border border-input rounded-md focus:outline-hidden focus:ring-2 focus:ring-ring"
                >
                  <option value="">All Actions</option>
                  {filterOptions?.actions.map((action) => (
                    <option key={action} value={action}>
                      {action.replace(/_/g, " ")}
                    </option>
                  ))}
                </select>
              </div>

              <div className="flex flex-col">
                <label className="mb-1 text-xs font-medium text-muted-foreground">Entity Type</label>
                <select
                  value={entityType}
                  onChange={(e) => handleFilterChange("entityType", e.target.value)}
                  className="px-3 py-2 text-sm bg-background border border-input rounded-md focus:outline-hidden focus:ring-2 focus:ring-ring"
                >
                  <option value="">All Entity Types</option>
                  {filterOptions?.entityTypes.map((type) => (
                    <option key={type} value={type}>
                      {type}
                    </option>
                  ))}
                </select>
              </div>

              <div className="flex flex-col">
                <label className="mb-1 text-xs font-medium text-muted-foreground">User ID</label>
                <input
                  type="text"
                  placeholder="Filter by User ID"
                  value={userId}
                  onChange={(e) => handleFilterChange("userId", e.target.value)}
                  className="px-3 py-2 text-sm bg-background border border-input rounded-md focus:outline-hidden focus:ring-2 focus:ring-ring"
                />
              </div>

              <div className="flex flex-col">
                <label className="mb-1 text-xs font-medium text-muted-foreground">Entity ID</label>
                <input
                  type="text"
                  placeholder="Filter by Entity ID"
                  value={entityId}
                  onChange={(e) => handleFilterChange("entityId", e.target.value)}
                  className="px-3 py-2 text-sm bg-background border border-input rounded-md focus:outline-hidden focus:ring-2 focus:ring-ring"
                />
              </div>
            </div>
          </div>

          {/* Loading State */}
          {isLoading ? (
            <div className="flex justify-center py-8">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
          ) : (
            <>
              {/* Table */}
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-border">
                      <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                        Action
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                        Entity
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                        User
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                        IP Address
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                        Created
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                        Details
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {auditLogs?.logs.map((log) => (
                      <tr key={log.id} className="border-b border-border hover:bg-secondary/30 transition-colors">
                        <td className="px-4 py-3 text-sm">
                          <span className="inline-flex px-2 py-1 text-xs font-medium rounded-md bg-primary/10 text-primary">
                            {log.action.replace(/_/g, " ")}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-sm text-foreground">
                          {log.entityType && (
                            <div className="flex flex-col gap-1">
                              <span className="text-muted-foreground">{log.entityType}</span>
                              {log.entityId && (
                                <button
                                  onClick={() => handleFilterChange("entityId", log.entityId || "")}
                                  className="inline-flex px-2 py-0.5 text-xs font-mono rounded bg-secondary hover:bg-secondary/80 text-foreground w-fit transition-colors cursor-pointer"
                                >
                                  {log.entityId.substring(0, 8)}...
                                </button>
                              )}
                            </div>
                          )}
                        </td>
                        <td className="px-4 py-3 text-sm text-foreground">
                          <div className="flex flex-col gap-1">
                            <span>{(log.user as any)?.name || "Unknown"}</span>
                            {log.userId && (
                              <button
                                onClick={() => handleFilterChange("userId", log.userId || "")}
                                className="inline-flex px-2 py-0.5 text-xs font-mono rounded bg-secondary hover:bg-secondary/80 text-foreground w-fit transition-colors cursor-pointer"
                              >
                                {log.userId.substring(0, 8)}...
                              </button>
                            )}
                          </div>
                        </td>
                        <td className="px-4 py-3 text-sm text-muted-foreground">
                          <div className="flex flex-col gap-1">
                            <span>{log.ipAddress || "-"}</span>
                            {log.userAgent && (
                              <span className="text-xs truncate max-w-[200px]" title={log.userAgent}>
                                {log.userAgent.substring(0, 30)}...
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="px-4 py-3 text-sm text-muted-foreground">{formatDate(log.createdAt)}</td>
                        <td className="px-4 py-3 text-sm">
                          {log.metadata && Object.keys(log.metadata).length > 0 && (
                            <pre className="text-xs bg-secondary p-2 rounded overflow-auto max-h-[100px] max-w-[300px]">
                              {JSON.stringify(log.metadata, null, 2)}
                            </pre>
                          )}
                        </td>
                      </tr>
                    ))}
                    {auditLogs?.logs.length === 0 && (
                      <tr>
                        <td colSpan={6} className="px-4 py-8 text-center text-muted-foreground">
                          No audit logs found
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              <div className="flex items-center justify-between px-4 py-3 mt-4 border-t border-border">
                <div className="text-sm text-muted-foreground">
                  Showing page <span className="font-medium">{page}</span> of{" "}
                  <span className="font-medium">{auditLogs?.totalPages || 1}</span>
                  {auditLogs?.totalCount !== undefined && <span className="ml-2">({auditLogs.totalCount} total)</span>}
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => handlePageChange(page - 1)}
                    disabled={page === 1}
                    className="px-3 py-1.5 text-sm font-medium text-foreground bg-secondary hover:bg-secondary/80 rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Previous
                  </button>
                  <button
                    onClick={() => handlePageChange(page + 1)}
                    disabled={!auditLogs?.hasMore}
                    className="px-3 py-1.5 text-sm font-medium text-foreground bg-secondary hover:bg-secondary/80 rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Next
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  )
}

export default AuditLogsScreen
